import { Input } from '@app/app/_components/Input/Input';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { BeneficiaryFormState } from '@app/domain/model/policy/BeneficiaryFormState';
import { getPercentageOptions } from '@app/app/self-service/(wizard)/change-beneficiary-death/utils/getPercentageOptions';
import {
    calculateAvailableShare,
    getBeneficiarySharePercent,
} from '@app/app/self-service/(wizard)/change-beneficiary-death/utils/calculateAvailableShare';
import { Select } from '@app/app/_components/Input/Select/Select';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useTranslations } from 'next-intl';
import { ChangeEvent, useCallback, useMemo, memo } from 'react';
import { Address } from '@app/domain/model/Address';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Card } from '@app/app/_components/Card/Card';
import { ProfileOption } from '@app/domain/model/ProfileOption';
import { AddressInput } from '@app/app/_components/AddressInput/AddressInput';

interface Props {
    existingBeneficiaries: BeneficiaryData[];
    newBeneficiaries: BeneficiaryData[];
    editingBeneficiary: BeneficiaryData | null;
    saveAttempted: boolean;
    editingBeneficiaryId: string;
    beneficiaryFormState: BeneficiaryFormState;
    isEstonianEshop?: boolean;
    handleSetShare: (share: string, id: string) => void;
    shareValue: string;
    countryOptions: ProfileOption[];
    isLv: boolean;
}

export const BeneficiaryEditView = memo(function BeneficiaryEditView({
    existingBeneficiaries,
    newBeneficiaries,
    editingBeneficiary,
    saveAttempted,
    editingBeneficiaryId,
    beneficiaryFormState,
    isEstonianEshop = false,
    handleSetShare,
    shareValue,
    countryOptions,
    isLv,
}: Props) {
    const t = useTranslations();

    const actualBeneficiaryId = editingBeneficiary?.id || editingBeneficiaryId;

    const beneficiaryErrors = useMemo(() => {
        return beneficiaryFormState.validation.errors[actualBeneficiaryId] || {};
    }, [beneficiaryFormState.validation.errors, actualBeneficiaryId]);

    const { onFirstNameChange: handleFirstNameChange } = beneficiaryFormState;
    const { onLastNameChange: handleLastNameChange } = beneficiaryFormState;
    const { onPersonalCodeChange: handlePersonalCodeChange } = beneficiaryFormState;
    const { onAddressChange: handleAddressChange } = beneficiaryFormState;

    const onFirstNameChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => handleFirstNameChange(actualBeneficiaryId, event),
        [handleFirstNameChange, actualBeneficiaryId],
    );

    const onLastNameChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => handleLastNameChange(actualBeneficiaryId, event),
        [handleLastNameChange, actualBeneficiaryId],
    );

    const onPersonalCodeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => handlePersonalCodeChange(actualBeneficiaryId, event),
        [handlePersonalCodeChange, actualBeneficiaryId],
    );

    const onShareChange = useCallback(
        (value: string) => handleSetShare(value, actualBeneficiaryId),
        [actualBeneficiaryId, handleSetShare],
    );

    const onAddressChange = useCallback(
        (address: Address | null) => handleAddressChange(actualBeneficiaryId, address),
        [handleAddressChange, actualBeneficiaryId],
    );

    const allBeneficiaries = useMemo(
        () => [...existingBeneficiaries, ...newBeneficiaries],
        [existingBeneficiaries, newBeneficiaries],
    );

    const maxPermittedPercentage = useMemo(() => {
        return calculateAvailableShare({
            allBeneficiaries,
            editingBeneficiaryId: actualBeneficiaryId,
        });
    }, [allBeneficiaries, actualBeneficiaryId]);

    const currentBeneficiaryShare = useMemo(() => getBeneficiarySharePercent(editingBeneficiary), [editingBeneficiary]);

    const percentageOptions = useMemo(() => {
        return getPercentageOptions(maxPermittedPercentage, currentBeneficiaryShare);
    }, [maxPermittedPercentage, currentBeneficiaryShare]);

    const displayShareValue = useMemo(() => {
        if (shareValue) return shareValue;

        return currentBeneficiaryShare > 0 ? String(currentBeneficiaryShare) : '100';
    }, [shareValue, currentBeneficiaryShare]);

    const onEmailChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => beneficiaryFormState.onEmailChange(actualBeneficiaryId, event),
        [beneficiaryFormState, actualBeneficiaryId],
    );

    const onPhoneChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            beneficiaryFormState.onPhoneChange(actualBeneficiaryId, event);
        },
        [beneficiaryFormState, actualBeneficiaryId],
    );

    const phonePrefix = useMemo(() => {
        if (isEstonianEshop) return '+372';

        if (isLv) return '+371';

        return '';
    }, [isEstonianEshop, isLv]);

    return (
        <Card className="flex flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
                <Heading level="h4">
                    {!editingBeneficiaryId ? t('lifeInsurance.addNewBeneficiary') : t('lifeInsurance.editBeneficiary')}
                </Heading>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Input
                    label={t('profile.firstNameLabel')}
                    name="firstName"
                    value={editingBeneficiary?.firstName || ''}
                    onChange={onFirstNameChange}
                    error={saveAttempted ? (beneficiaryErrors?.firstName ?? undefined) : undefined}
                />

                <Input
                    label={t('profile.lastNameLabel')}
                    name="lastName"
                    value={editingBeneficiary?.lastName || ''}
                    onChange={onLastNameChange}
                    error={saveAttempted ? (beneficiaryErrors?.lastName ?? undefined) : undefined}
                />

                <Input
                    label={t('profile.personalCodeLabel')}
                    name="personalCode"
                    value={editingBeneficiary?.personalCode || ''}
                    onChange={onPersonalCodeChange}
                    error={saveAttempted ? (beneficiaryErrors?.personalCode ?? undefined) : undefined}
                />
                <Select
                    label={t('lifeInsurance.sharePercentage')}
                    name="share"
                    value={displayShareValue}
                    onChange={onShareChange}
                    error={saveAttempted ? (beneficiaryErrors?.share ?? undefined) : undefined}
                    options={percentageOptions}
                />
            </div>
            <Heading level="h4">{t('lifeInsurance.beneficiaryContactInformation')}</Heading>
            {isEstonianEshop || isLv ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                        label={t('eshop.life.emailAddress')}
                        placeholder={t('profile.emailPlaceholder')}
                        name="email"
                        type="email"
                        value={editingBeneficiary?.email || ''}
                        onChange={onEmailChange}
                        error={saveAttempted ? (beneficiaryErrors?.email ?? undefined) : undefined}
                    />
                    <Input
                        label={t('profile.mobilePhoneLabel')}
                        placeholder={t('eshop.life.phonePlaceholder')}
                        prefix={phonePrefix}
                        prefixClassName="bg-neutral-50 text-neutral-500"
                        name="phone"
                        type="tel"
                        noSpaces
                        value={editingBeneficiary?.phone || ''}
                        onChange={onPhoneChange}
                        error={saveAttempted ? (beneficiaryErrors?.phone ?? undefined) : undefined}
                    />
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    <AddressInput
                        onChange={onAddressChange}
                        countryOptions={countryOptions}
                        defaultValue={editingBeneficiary?.address ?? undefined}
                        forceSearchOnStart
                    />
                    {saveAttempted && isViolationMessage(beneficiaryErrors?.address ?? undefined) && (
                        <Paragraph size="small" className="text-red-500">
                            {t(beneficiaryErrors?.address?.message)}
                        </Paragraph>
                    )}
                </div>
            )}
        </Card>
    );
});
